import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  Grid
} from '@mui/material';
import i18n from 'i18next';
import { ThemeContext } from './Theme/ThemeContext';
import enTranslations from '../language-en.json';
import arTranslations from '../language-ar.json';

export default function LanguageSelectionModal({ openOverride, onCloseOverride }) {
  const { isDark } = useContext(ThemeContext);
  const [open, setOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState('en');
  const [isInitialLaunch, setIsInitialLaunch] = useState(false);

  // Load translations into i18n
  useEffect(() => {
    i18n.addResourceBundle('en', 'translations', enTranslations, true, true);
    i18n.addResourceBundle('ar', 'translations', arTranslations, true, true);
  }, []);

  useEffect(() => {
    // The app-download smart link must redirect instantly on scan - never block it
    // behind a language prompt, especially for first-time scanners with no localStorage yet.
    if (window.location.pathname.startsWith('/download')) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const hasSerial = urlParams.get('serial') || urlParams.get('qr') || (window.location.pathname.length > 2 && window.location.pathname !== '/');
    const hasSelectedSession = sessionStorage.getItem('has_selected_language_session');
    const hasSelectedLocal = localStorage.getItem('has_selected_language');

    if (openOverride !== undefined) {
      setOpen(openOverride);
      setIsInitialLaunch(false);
    } else if (hasSerial && !hasSelectedSession) {
      setOpen(true);
      setIsInitialLaunch(true);
    } else if (!hasSelectedLocal) {
      setOpen(true);
      setIsInitialLaunch(true);
    }

    const savedLang = localStorage.getItem('lang');
    if (savedLang) {
      try {
        const parsed = JSON.parse(savedLang);
        if (parsed?.langLocale) {
          setSelectedLang(parsed.langLocale);
        }
      } catch (e) {
        console.error("Failed to parse saved lang:", e);
      }
    }
  }, [openOverride]);

  const handleSelectLanguage = (langCode) => {
    setSelectedLang(langCode);
    i18n.changeLanguage(langCode);
    document.documentElement.dir = langCode === 'ar' ? 'rtl' : 'ltr';
  };

  const handleConfirm = () => {
    const langObj = {
      langLocale: selectedLang,
      dateLocale: selectedLang
    };
    localStorage.setItem('lang', JSON.stringify(langObj));
    localStorage.setItem('has_selected_language', 'true');
    sessionStorage.setItem('has_selected_language_session', 'true');

    i18n.changeLanguage(selectedLang);
    document.documentElement.dir = selectedLang === 'ar' ? 'rtl' : 'ltr';

    setOpen(false);
    setIsInitialLaunch(false);
    if (onCloseOverride) {
      onCloseOverride();
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Full-screen opaque mask hiding website until language is selected */}
      {isInitialLaunch && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: '#090a0f',
          zIndex: 999998
        }} />
      )}

      <Dialog
        open={open}
        sx={{ zIndex: 999999 }}
        onClose={(event, reason) => {
          if (localStorage.getItem('has_selected_language')) {
            setOpen(false);
            if (onCloseOverride) onCloseOverride();
          }
        }}
        disableEscapeKeyDown={!localStorage.getItem('has_selected_language')}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '28px',
            bgcolor: isDark ? '#121218' : '#ffffff',
            color: isDark ? '#ffffff' : '#0f172a',
            p: 2,
            direction: 'ltr',
            boxShadow: '0 24px 48px rgba(0,0,0,0.9)',
            border: '1.5px solid',
            borderColor: isDark ? 'rgba(233, 174, 49, 0.3)' : 'rgba(233, 174, 49, 0.2)'
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 3, pb: 2, direction: 'ltr' }}>
          <Box sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'rgba(233, 174, 49, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2.5,
            fontSize: '30px'
          }}>
            🌐
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, mb: 3.5, fontFamily: 'Outfit, sans-serif' }}>
            {selectedLang === 'ar' ? 'اختر اللغة' : 'Select Language'}
          </Typography>

          {/* Only 2 clean language buttons */}
          <Grid container spacing={2} sx={{ mb: 3.5, direction: 'ltr' }}>
            {/* English Button */}
            <Grid item xs={6}>
              <Box
                onClick={() => handleSelectLanguage('en')}
                sx={{
                  cursor: 'pointer',
                  py: 2.5,
                  px: 2,
                  borderRadius: '20px',
                  border: '2px solid',
                  borderColor: selectedLang === 'en' ? '#E9AE31' : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'),
                  bgcolor: selectedLang === 'en' ? (isDark ? 'rgba(233, 174, 49, 0.18)' : 'rgba(233, 174, 49, 0.12)') : 'transparent',
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#E9AE31'
                  }
                }}
              >
                <Typography variant="h3" sx={{ mb: 1, fontWeight: 900, fontSize: '28px', color: selectedLang === 'en' ? '#E9AE31' : 'inherit', letterSpacing: '1px' }}>ENG</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: '16px' }}>English</Typography>
              </Box>
            </Grid>

            {/* Arabic Button */}
            <Grid item xs={6}>
              <Box
                onClick={() => handleSelectLanguage('ar')}
                sx={{
                  cursor: 'pointer',
                  py: 2.5,
                  px: 2,
                  borderRadius: '20px',
                  border: '2px solid',
                  borderColor: selectedLang === 'ar' ? '#E9AE31' : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'),
                  bgcolor: selectedLang === 'ar' ? (isDark ? 'rgba(233, 174, 49, 0.18)' : 'rgba(233, 174, 49, 0.12)') : 'transparent',
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#E9AE31'
                  }
                }}
              >
                <Typography variant="h3" sx={{ mb: 1, fontWeight: 900, fontSize: '28px', color: selectedLang === 'ar' ? '#E9AE31' : 'inherit', letterSpacing: '1px' }}>AR</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: '16px' }}>العربية</Typography>
              </Box>
            </Grid>
          </Grid>

          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirm}
            sx={{
              py: 1.8,
              borderRadius: '16px',
              bgcolor: '#E9AE31',
              color: '#000000',
              fontWeight: 800,
              fontSize: '16px',
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(233, 174, 49, 0.3)',
              '&:hover': {
                bgcolor: '#d89e22'
              }
            }}
          >
            {selectedLang === 'ar' ? 'متابعة' : 'Continue'}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
